/**
 * IDEAM BART composite georeferenced map overlay (Milestone 4 + calibration).
 *
 * The BART frame is a national Colombia radar composite PNG. Mapbox image sources
 * expect corner coordinates in order: top-left, top-right, bottom-right, bottom-left.
 *
 * All bounds changes must flow through this module (or REACT_APP_IDEAM_BART_BOUNDS).
 */

import {
  boundsToGeoJson,
  boundsToMapboxCoordinates,
  parseBoundsEnv,
} from "../utils/ideamBartBoundsUtils.js";

export const IDEAM_BART_RADAR_SOURCE_ID = "weather-radar-ideam-bart-source";
export const IDEAM_BART_RADAR_LAYER_ID = "weather-radar-ideam-bart-layer";
export const IDEAM_BART_RADAR_BOUNDS_SOURCE_ID = "weather-radar-ideam-bart-bounds-source";
export const IDEAM_BART_RADAR_BOUNDS_LAYER_ID = "weather-radar-ideam-bart-bounds-layer";
export const IDEAM_BART_REF_MARKERS_SOURCE_ID = "weather-radar-ideam-bart-ref-markers-source";
export const IDEAM_BART_REF_MARKERS_LAYER_ID = "weather-radar-ideam-bart-ref-markers-layer";
export const IDEAM_BART_CORNER_LABELS_SOURCE_ID = "weather-radar-ideam-bart-corner-labels-source";
export const IDEAM_BART_CORNER_LABELS_LAYER_ID = "weather-radar-ideam-bart-corner-labels-layer";

export const IDEAM_BART_COMPOSITE_SOURCE_KEY = "ideam_bart_composite";

/** First-pass Colombia bounding box — calibration may be required. */
export const IDEAM_BART_COLOMBIA_BOUNDS = {
  westLng: -82.2,
  southLat: -4.5,
  eastLng: -66.8,
  northLat: 13.8,
};

export const IDEAM_BART_OVERLAY_CONFIG = {
  source: IDEAM_BART_COMPOSITE_SOURCE_KEY,
  sourceLabel: "IDEAM Imagen Compuesta",
  trustLabel: "Official",
  product: "composite_basemap",
  attribution: "IDEAM",
  updateIntervalMinutes: 5,
  bounds: IDEAM_BART_COLOMBIA_BOUNDS,
  defaultOpacity: 0.72,
};

export const IDEAM_BART_CALIBRATION_CONFIG = {
  source: IDEAM_BART_COMPOSITE_SOURCE_KEY,
  sourceLabel: IDEAM_BART_OVERLAY_CONFIG.sourceLabel,
  bounds: IDEAM_BART_COLOMBIA_BOUNDS,
  mapboxCoordinates: boundsToMapboxCoordinates(IDEAM_BART_COLOMBIA_BOUNDS),
  attribution: IDEAM_BART_OVERLAY_CONFIG.attribution,
  calibrationVersion: "initial",
  updatedAt: null,
  notes: "Initial Colombia national composite bounds.",
};

export function resolveIdeamBartColombiaBounds() {
  return parseBoundsEnv(process.env.REACT_APP_IDEAM_BART_BOUNDS) || IDEAM_BART_COLOMBIA_BOUNDS;
}

export function getIdeamBartMapboxCoordinates(bounds = resolveIdeamBartColombiaBounds()) {
  return boundsToMapboxCoordinates(bounds);
}

export function getIdeamBartBoundsGeoJson(
  bounds = resolveIdeamBartColombiaBounds(),
  properties = { debug: true, label: "IDEAM BART bounds (debug)" }
) {
  return boundsToGeoJson(bounds, properties);
}

export function isIdeamBartOverlayPreviewAvailable() {
  return process.env.REACT_APP_IDEAM_BART_OVERLAY_PREVIEW_ENABLED !== "false";
}

export function isIdeamBartDebugBoundsEnabled(options = {}) {
  if (options.forceEnabled) {
    return true;
  }

  if (process.env.NODE_ENV === "production") {
    return false;
  }

  if (process.env.REACT_APP_IDEAM_BART_DEBUG_BOUNDS === "true") {
    return true;
  }

  if (typeof window !== "undefined" && window.__MIDPOINT_BART_DEBUG_BOUNDS__ === true) {
    return true;
  }

  return false;
}
